import { useState, useEffect, useMemo, useRef } from "react";
import { fetchTeamLineupUsage } from "../../../services/team-service";

export const useLineupUsage = ({ teamId, years }) => {
  const [data, setData] = useState(null);
  const [status, setStatus] = useState("loading");
  const [selectedYear, setSelectedYear] = useState(years?.[0] ?? null);
  const isInitializingRef = useRef(true);
  const skipNextEffectRef = useRef(false);

  useEffect(() => {
    if (skipNextEffectRef.current) {
      skipNextEffectRef.current = false;
      return;
    }

    const controller = new AbortController();

    (async () => {
      setStatus("loading");
      try {
        const res = await fetchTeamLineupUsage(teamId, selectedYear, controller.signal);
        if (controller.signal.aborted) return;

        setData(res);

        if (isInitializingRef.current && !selectedYear && res?.available_years?.length) {
          isInitializingRef.current = false;
          skipNextEffectRef.current = true;
          if (!controller.signal.aborted) setSelectedYear(res.available_years[0]);
        } else {
          isInitializingRef.current = false;
        }

        if (!controller.signal.aborted) setStatus("ready");
      } catch (e) {
        if (e?.name !== "AbortError") {
          setStatus("error");
          isInitializingRef.current = false;
        }
      }
    })();

    return () => controller.abort();
  }, [teamId, selectedYear]);

  const availableYears = useMemo(() => data?.available_years || years || [], [data, years]);

  const { starters, bench, totalGames } = useMemo(() => {
    const batters = data?.batters || [];
    if (!batters.length) return { starters: [], bench: [], totalGames: 1 };
    const tg = batters[0]?.total_games ?? 1;
    const threshold = Math.ceil(tg * 0.5);
    return {
      totalGames: tg,
      starters: batters.filter((b) => (b.games ?? 0) >= threshold),
      bench: batters.filter((b) => (b.games ?? 0) < threshold && (b.games ?? 0) >= 2),
    };
  }, [data]);

  return { status, selectedYear, setSelectedYear, availableYears, starters, bench, totalGames };
};
